import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errDetail, fmtIST } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TestBadge, EmptyState, ErrorState, ListSkeleton, BookingStateBadge, VoiceStateBadge } from "@/components/common";
import { OnboardingWizardDialog } from "@/components/OnboardingWizard";
import { IncludeTestToggle } from "@/components/IncludeTestToggle";
import { useIncludeTest, withIncludeTest } from "@/hooks/useIncludeTest";
import { cappedLength } from "@/lib/counts";
import { PagedFooter, DateRangeBar } from "@/components/PagedFooter";
import { unwrapPage, pagedQuery, exclusiveTo, PAGE_SIZE } from "@/lib/paging";
import { Inbox, Plus, Upload, UserCheck, AlertTriangle } from "lucide-react";

const DEDUP_LABELS = {
  new_contact: { label: "New customer", cls: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30" },
  attached_existing_opportunity: { label: "Attached to existing", cls: "bg-[hsl(var(--info))]/10 text-[hsl(var(--info))] border-[hsl(var(--info))]/20" },
  new_opportunity: { label: "New opportunity", cls: "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] border-transparent" },
  new_opportunity_repeat: { label: "Repeat customer", cls: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30" },
  new_opportunity_cross_product: { label: "Cross-product", cls: "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] border-transparent" },
  possible_duplicate: { label: "Possible duplicate", cls: "bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))] border-[hsl(var(--danger))]/30" },
};

const DedupBadge = ({ result }) => {
  const d = DEDUP_LABELS[result] || { label: result, cls: "bg-muted" };
  return <Badge variant="outline" className={`border text-[11px] ${d.cls}`}>{d.label}</Badge>;
};

// AI Voice operational state — the badge itself is the shared
// <VoiceStateBadge> (components/common.jsx), used identically here, on
// Calls and on Customer 360. This cell just adds the Lead-Pool sub-line
// (attempt no / next attempt / ownership). Backend-derived; never a
// second source of truth.
const VoiceStateCell = ({ vs }) => {
  if (!vs) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <div className="space-y-0.5">
      <VoiceStateBadge vs={vs} />
      <div className="text-[10px] text-muted-foreground leading-tight">
        {vs.attempt_no ? `Attempt ${vs.attempt_no}` : null}
        {vs.attempt_no && vs.next_attempt_at ? " · " : null}
        {vs.next_attempt_at ? `next ${fmtIST(vs.next_attempt_at)}` : null}
        {vs.owns_outreach ? (vs.attempt_no || vs.next_attempt_at ? " · AI owns outreach" : "AI owns outreach") : null}
      </div>
    </div>
  );
};

const ImportDialog = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [isTest, setIsTest] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/leads/import?is_test_record=${isTest}`, fd);
      setResult(data);
      onDone();
    } catch (e) {
      toast.error(errDetail(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setResult(null); }}>
      <DialogTrigger asChild>
        <Button data-testid="import-csv-button" variant="outline"><Upload className="h-4 w-4 mr-1.5" /> Import CSV</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">Import leads from CSV</DialogTitle>
          <DialogDescription>Columns: name, phone, email, product_code, source, campaign. Every row passes through dedup — the import never creates duplicate customers.</DialogDescription>
        </DialogHeader>
        {result ? (
          <div data-testid="import-result" className="text-sm space-y-1">
            <p>New customers: <b>{result.created}</b></p>
            <p>Attached to existing opportunities: <b>{result.attached}</b></p>
            <p>New opportunities (repeat/cross-product): <b>{result.new_opportunity}</b></p>
            <p>Possible duplicates for review: <b>{result.possible_duplicate}</b></p>
            {result.errors.length > 0 && <p className="text-[hsl(var(--danger))]">Errors: {result.errors.length}</p>}
          </div>
        ) : (
          <div className="space-y-3">
            <Input data-testid="import-file-input" type="file" accept=".csv" onChange={(e) => setFile(e.target.files?.[0])} />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={isTest} onChange={(e) => setIsTest(e.target.checked)} />
              Import as TEST records
            </label>
          </div>
        )}
        <DialogFooter>
          {!result && <Button data-testid="import-submit-button" onClick={submit} disabled={!file || busy}>{busy ? "Importing…" : "Import"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function LeadsPage() {
  const [params] = useSearchParams();
  const { hasCap } = useAuth();
  // R14 closure (September visibility): both lists are PAGED pages ({items, total, offset, has_more}) with an IST received-date range, so an
  // older (e.g. September) enquiry is always reachable - the lists used to show only the newest 200 / 100 rows with no way further back.
  const [pool, setPool] = useState(null);
  const [intakes, setIntakes] = useState(null);
  const [poolF, setPoolF] = useState({ from: "", to: "", page: 0 });
  const [intakeF, setIntakeF] = useState({ from: "", to: "", q: "", page: 0 });
  const [dups, setDups] = useState(null);
  const [counts, setCounts] = useState(null);
  // R7: a failed request is an error with Retry, never an endless skeleton or a
  // false "nothing here" (keyed by the tab's data source; cleared on success).
  const [loadErr, setLoadErr] = useState({});
  const [employees, setEmployees] = useState([]);
  const [takeOverTarget, setTakeOverTarget] = useState(null); // pool row
  const [takeOverUser, setTakeOverUser] = useState("");
  const [takeOverBusy, setTakeOverBusy] = useState(false);
  const [tab, setTab] = useState(params.get("tab") || "pool");
  const canAssign = hasCap("crm.assign");
  // Round 7 Workstream F - TEST is hidden unless an authorised admin turns the
  // visible toggle on; the pool, the intakes and the counters all take the SAME
  // flag, so the summary and the tab can never disagree about it.
  const { includeTest, canInclude, setIncludeTest } = useIncludeTest();

  const loadPool = useCallback(() => {
    const offset = poolF.page * PAGE_SIZE;
    const qs = pagedQuery({ offset, extra: { received_from: poolF.from, received_to: exclusiveTo(poolF.to) } });
    api.get(withIncludeTest(`/leads/pool?${qs}`, includeTest))
      .then((r) => { setPool(unwrapPage(r.data, offset)); setLoadErr((e) => ({ ...e, pool: false })); })
      .catch(() => setLoadErr((e) => ({ ...e, pool: true })));
  }, [includeTest, poolF]);

  const loadIntakes = useCallback(() => {
    const offset = intakeF.page * PAGE_SIZE;
    const qs = pagedQuery({ offset, extra: { received_from: intakeF.from, received_to: exclusiveTo(intakeF.to), q: intakeF.q.trim() } });
    api.get(withIncludeTest(`/leads?${qs}`, includeTest))
      .then((r) => { setIntakes(unwrapPage(r.data, offset)); setLoadErr((e) => ({ ...e, intakes: false })); })
      .catch(() => setLoadErr((e) => ({ ...e, intakes: true })));
  }, [includeTest, intakeF]);

  const loadOthers = useCallback(() => {
    api.get(withIncludeTest("/leads/counts", includeTest)).then((r) => setCounts(r.data)).catch(() => {});
    if (hasCap("duplicates.review")) {
      api.get("/leads/duplicates").then((r) => { setDups(r.data); setLoadErr((e) => ({ ...e, dups: false })); }).catch(() => setLoadErr((e) => ({ ...e, dups: true })));
    }
  }, [hasCap, includeTest]);

  const load = useCallback(() => { loadPool(); loadIntakes(); loadOthers(); }, [loadPool, loadIntakes, loadOthers]);

  useEffect(() => { loadOthers(); }, [loadOthers]);
  useEffect(() => { loadPool(); }, [loadPool]);
  // the search box is debounced; a date / page change loads at once
  useEffect(() => { const t = setTimeout(loadIntakes, intakeF.q ? 300 : 0); return () => clearTimeout(t); }, [loadIntakes, intakeF.q]);
  // a filter change ALWAYS returns to the first page (never an empty page 7 of a narrower result)
  const changePool = (patch) => setPoolF((f) => ({ ...f, ...patch, page: 0 }));
  const changeIntakes = (patch) => setIntakeF((f) => ({ ...f, ...patch, page: 0 }));
  // Round 7 Workstream E - the pool and its counters refetch on regaining
  // focus/visibility (same pattern as Home), so an assignment made from another
  // tab / Customer 360 / Approvals never leaves this view showing a stale number.
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("focus", load);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", load);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [load]);
  useEffect(() => {
    if (canAssign) api.get("/users").then((r) => setEmployees((r.data.users || []).filter((u) => u.is_active))).catch(() => {});
  }, [canAssign]);

  const claim = async (o) => {
    // Spec Section C — don't let a rep quietly place a manual call while
    // AI Voice genuinely owns this lead's outreach. Confirm first; the
    // deliberate, safe interrupt is "Take over from AI".
    if (o.voice_state?.owns_outreach
        && !window.confirm("AI Voice is currently handling this lead. Claiming it does NOT stop the AI. "
          + "Use “Take over from AI” instead if you want to interrupt it. Claim anyway?")) {
      return;
    }
    try {
      await api.post(`/leads/claim/${o.opportunity_id}`);
      toast.success("Lead claimed — it's yours now");
      load();
    } catch (e) {
      toast.error(errDetail(e, "Could not claim"));
      load();
    }
  };

  const submitTakeOver = async () => {
    if (!takeOverTarget || !takeOverUser) return;
    setTakeOverBusy(true);
    try {
      const { data } = await api.post(`/leads/${takeOverTarget.opportunity_id}/take-over-from-ai`,
        { owner_user_id: takeOverUser, expected_current_owner_user_id: takeOverTarget.owner_user_id || null });
      toast.success(`Taken over from AI — assigned to ${data.owner}`
        + (data.lease_released ? " · Voice slot released" : "")
        + (data.jobs_cancelled ? ` · ${data.jobs_cancelled} pending AI job(s) cancelled` : ""));
      setTakeOverTarget(null); setTakeOverUser(""); load();
    } catch (e) {
      toast.error(errDetail(e, "Could not take over"));
      load();
    } finally {
      setTakeOverBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="leads-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Leads</h1>
          <p className="text-sm text-muted-foreground">Every enquiry becomes a Lead Intake — dedup keeps one customer truth.</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <IncludeTestToggle includeTest={includeTest} canInclude={canInclude} onChange={setIncludeTest} testId="leads-include-test" />
          {hasCap("leads.import") && <ImportDialog onDone={load} />}
          {hasCap("crm.create") && <OnboardingWizardDialog onCreated={load} triggerLabel="New lead" triggerTestId="new-lead-button" />}
        </div>
      </div>

      {counts && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="leads-counts">
          {[
            ["Canonical customers", counts.canonical_customers, "One row per real person (dedup keeps one truth)."],
            ["Active opportunities", counts.active_opportunities, "Product-level opportunities that are live now. One customer can hold several."],
            ["Unassigned leads (lead pool)", counts.unassigned_opportunities, "Active opportunities with no owner yet. Unassigned Cases and unassigned tasks are separate numbers (Approvals -> Unassigned)."],
            ["Recent intake events", counts.recent_intake_events, "Every inbound submission (Meta / Sheets / CSV / manual), including repeats that matched an existing customer."],
          ].map(([label, value, hint]) => (
            <Card key={label} className="p-3">
              <div className="text-xs text-muted-foreground" title={hint}>{label}</div>
              <div className="text-xl font-semibold tabular-nums">{value ?? "—"}</div>
            </Card>
          ))}
        </div>
      )}
      {counts && (
        <p className="text-[11px] text-muted-foreground -mt-2">
          These totals are meant to differ — one customer can have several opportunities, and one
          opportunity can follow several intake events (repeat enquiries, duplicates).
          {counts.unassigned_opportunities_capped && (
            <> The pool tab pages through all {counts.unassigned_opportunities} (50 per page) — use Next, or a received-date range, to reach older leads.</>
          )}
          {canInclude && !includeTest && counts.unassigned_opportunities_test > 0 && (
            <span data-testid="leads-test-hidden-note"> {counts.unassigned_opportunities_test} TEST lead{counts.unassigned_opportunities_test === 1 ? "" : "s"} in the pool {counts.unassigned_opportunities_test === 1 ? "is" : "are"} hidden — turn on “Include TEST” to see {counts.unassigned_opportunities_test === 1 ? "it" : "them"}.</span>
          )}
          {includeTest && counts.unassigned_opportunities_test > 0 && (
            <span data-testid="leads-test-included-note"> The pool count includes {counts.unassigned_opportunities_test} TEST record{counts.unassigned_opportunities_test === 1 ? "" : "s"} (business: {counts.unassigned_opportunities - counts.unassigned_opportunities_test}).</span>
          )}
        </p>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="leads-tabs-list" className="flex-wrap h-auto justify-start gap-0.5">
          <TabsTrigger data-testid="leads-tab-pool" value="pool">Lead pool {pool && <Badge variant="outline" className="ml-1.5 text-[10px]" data-testid="leads-pool-badge">{counts ? counts.unassigned_opportunities : (pool.total !== null ? pool.total : cappedLength(pool.items.length, 200))}</Badge>}</TabsTrigger>
          <TabsTrigger data-testid="leads-tab-intakes" value="intakes">Recent intakes</TabsTrigger>
          {hasCap("duplicates.review") && (
            <TabsTrigger data-testid="leads-tab-duplicates" value="duplicates">
              Possible duplicates {dups && dups.length > 0 && <Badge className="ml-1.5 text-[10px] bg-[hsl(var(--danger))]">{dups.length}</Badge>}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="pool">
          <Card>
            <DateRangeBar testId="leads-pool-range" showSearch={false} from={poolF.from} to={poolF.to}
              onChange={changePool} onClear={() => setPoolF({ from: "", to: "", page: 0 })}
              hint="Received = when the enquiry reached SalesOS. Pick a date range (IST) to reach older leads; the list is paged, never silently cut off." />
            {!pool ? (loadErr.pool
              ? <ErrorState testId="leads-pool-error" title="Couldn't load the lead pool" onRetry={load} />
              : <ListSkeleton rows={3} testId="leads-pool-loading" />)
              : pool.items.length === 0 ? (poolF.from || poolF.to
                ? <EmptyState icon={Inbox} title="No unclaimed leads in that date range" hint="Clear the dates to see the whole pool." />
                : <EmptyState icon={Inbox} title="Lead pool is empty" hint="Unclaimed leads land here. Claiming is atomic — only one person can win a lead." />)
                : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Customer</TableHead>
                        <TableHead>Product</TableHead>
                        <TableHead className="hidden md:table-cell">Source</TableHead>
                        <TableHead>AI Voice</TableHead>
                        <TableHead className="hidden md:table-cell">Booking</TableHead>
                        <TableHead className="hidden lg:table-cell">Received</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pool.items.map((o) => (
                        <TableRow key={o.opportunity_id} data-testid={`pool-row-${o.opportunity_id}`}>
                          <TableCell>
                            <Link to={`/customers/${o.contact_id}`} className="flex items-center gap-2 hover:underline">
                              <span className="font-medium">{o.contact?.full_name}</span>
                              <TestBadge show={o.is_test_record} />
                            </Link>
                            <span className="font-mono text-[11px] text-muted-foreground">{o.contact?.customer_id}</span>
                          </TableCell>
                          <TableCell className="text-sm">{o.product_name}</TableCell>
                          <TableCell className="hidden md:table-cell text-sm text-muted-foreground">{o.first_touch_source}</TableCell>
                          <TableCell><VoiceStateCell vs={o.voice_state} /></TableCell>
                          {/* Booking column shows genuine booking info only.
                              "Human follow-up required" is a Voice/automation
                              operational state, already shown prominently in the
                              AI Voice column — not duplicated here (booking_state
                              itself is unchanged; this is display-only). */}
                          <TableCell className="hidden md:table-cell">
                            {o.booking_state === "human_followup_required"
                              ? <span className="text-xs text-muted-foreground">Not booked</span>
                              : <BookingStateBadge state={o.booking_state} />}
                          </TableCell>
                          <TableCell className="hidden lg:table-cell text-xs text-muted-foreground">{fmtIST(o.created_at)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1.5">
                              {canAssign && o.voice_state?.takeover_available && (
                                <Button data-testid={`takeover-button-${o.opportunity_id}`} size="sm" variant="outline"
                                  onClick={() => { setTakeOverTarget(o); setTakeOverUser(""); }}>
                                  Take over from AI
                                </Button>
                              )}
                              <Button data-testid={`claim-button-${o.opportunity_id}`} size="sm" onClick={() => claim(o)}>
                                <UserCheck className="h-3.5 w-3.5 mr-1" /> Claim
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
            {pool && !loadErr.pool && <PagedFooter page={pool} testId="leads-pool"
              onPrev={() => setPoolF((f) => ({ ...f, page: Math.max(0, f.page - 1) }))}
              onNext={() => setPoolF((f) => ({ ...f, page: f.page + 1 }))} />}
          </Card>
        </TabsContent>

        <TabsContent value="intakes">
          <Card>
            <DateRangeBar testId="leads-intakes-range" from={intakeF.from} to={intakeF.to} q={intakeF.q}
              onChange={changeIntakes} onClear={() => setIntakeF({ from: "", to: "", q: "", page: 0 })}
              hint="Received = the day the enquiry reached SalesOS (the original intake date). A September enquiry that was matched to a customer or converted later still shows its September received date - the customer's own Created date can be later." />
            {!intakes ? (loadErr.intakes
              ? <ErrorState testId="leads-intakes-error" title="Couldn't load recent intakes" onRetry={load} />
              : <ListSkeleton rows={3} testId="leads-intakes-loading" />)
              : intakes.items.length === 0 ? (intakeF.from || intakeF.to || intakeF.q
                ? <EmptyState icon={Inbox} title="No intakes match these filters" hint="Change the dates or clear the search." />
                : <EmptyState icon={Inbox} title="No lead intakes yet" />)
                : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Product</TableHead>
                        <TableHead className="hidden md:table-cell">Source / Channel</TableHead>
                        <TableHead>Dedup result</TableHead>
                        <TableHead className="hidden lg:table-cell">Received</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {intakes.items.map((l) => (
                        <TableRow key={l.lead_id}>
                          <TableCell>
                            <Link to={`/customers/${l.contact_id}`} className="flex items-center gap-2 hover:underline">
                              <span className="font-medium text-sm">{l.name}</span>
                              <TestBadge show={l.is_test_record} />
                            </Link>
                          </TableCell>
                          <TableCell className="text-sm">{l.product_code}</TableCell>
                          <TableCell className="hidden md:table-cell text-sm text-muted-foreground">{l.source} · {l.channel}</TableCell>
                          <TableCell><DedupBadge result={l.dedup_result} /></TableCell>
                          <TableCell className="hidden lg:table-cell text-xs text-muted-foreground">{fmtIST(l.created_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
            {intakes && !loadErr.intakes && <PagedFooter page={intakes} testId="leads-intakes"
              onPrev={() => setIntakeF((f) => ({ ...f, page: Math.max(0, f.page - 1) }))}
              onNext={() => setIntakeF((f) => ({ ...f, page: f.page + 1 }))} />}
          </Card>
        </TabsContent>

        <TabsContent value="duplicates">
          <Card>
            {!dups && loadErr.dups ? (
              <ErrorState testId="leads-dups-error" title="Couldn't load possible duplicates" onRetry={load} />
            ) : !dups || dups.length === 0 ? (
              <EmptyState icon={AlertTriangle} title="No possible duplicates awaiting review" hint="When phone and email point to different people, the lead is flagged here instead of guessing." />
            ) : dups.map((d) => (
              <div key={d.contact.contact_id} className="p-4 border-b last:border-0">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-[hsl(var(--danger))]" />
                  <Link to={`/customers/${d.contact.contact_id}`} className="font-medium hover:underline">{d.contact.full_name}</Link>
                  <span className="font-mono text-xs text-muted-foreground">{d.contact.customer_id}</span>
                  <TestBadge show={d.contact.is_test_record} />
                </div>
                <p className="text-xs text-muted-foreground mt-1">Conflicting identity — may be the same person as:</p>
                <div className="flex gap-2 mt-1 flex-wrap">
                  {d.candidates.map((c) => (
                    <Link key={c.contact_id} to={`/customers/${c.contact_id}`}>
                      <Badge variant="outline" className="hover:bg-muted">{c.full_name} · {c.customer_id}</Badge>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!takeOverTarget} onOpenChange={(o) => { if (!o) { setTakeOverTarget(null); setTakeOverUser(""); } }}>
        <DialogContent className="sm:max-w-md" data-testid="takeover-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Take over from AI Voice</DialogTitle>
            <DialogDescription>
              AI Voice is currently handling <b>{takeOverTarget?.contact?.full_name}</b>
              {takeOverTarget?.voice_state?.label ? ` (${takeOverTarget.voice_state.label})` : ""}.
              This safely stops any pending AI attempt, frees the Voice slot if held, keeps the
              full Voice history, and hands the customer to the person you pick.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label className="text-xs">Hand over to</Label>
            <Select value={takeOverUser} onValueChange={setTakeOverUser}>
              <SelectTrigger data-testid="takeover-target"><SelectValue placeholder="Pick employee" /></SelectTrigger>
              <SelectContent>
                {employees.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.role})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button data-testid="takeover-confirm" disabled={!takeOverUser || takeOverBusy} onClick={submitTakeOver}>
              {takeOverBusy ? "Taking over…" : "Take over from AI"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
